import InfoBadge from '@liveauctioneers/caterwaul-components/lib/Badges/InfoBadge';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import loadable from '@loadable/component';
import * as React from 'react';
import styled from 'styled-components';

import ConsoleSocialBadge from '@/components/ConsoleSocial/ConsoleSocialBadge';
import { usePrevious } from '@/hooks/usePrevious';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getCatalogAbsenteeBidders } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { fetchLiveCatalogRegistrationByIdIfNeeded } from '@/redux/modules/catalog/registration/catalogRegistration.actions';
import {
    getLiveCatalogBidders,
    getLiveCatalogCurrentItemId,
    getLiveCatalogCurrentItemIndex,
    getLiveCatalogPaused,
} from '@/redux/modules/console';
import {
    getItemSummaryCatalogId,
    getItemSummaryCatalogStatus,
    getItemSummaryIndex,
    getItemSummaryIsCatalogOnly,
    getItemSummaryPromoted,
    getItemSummarySaleStartTs,
    getItemSummarySellerId,
    getItemSummaryTitle,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { fetchCatalogPresence, fetchLiveCatalogStatusIfNeeded } from '@/redux/modules/liveCatalogStatus';
import { AuctionStatus } from '@/types/LiveAuctionState';
import { shouldShowLive } from '@/utils/live';
import noop from '@/utils/noop';

import CardCta from './CardCta';
import ItemCardMenu from './components/Menu/ItemCardMenu';
import DetailsRow from './DetailsRow';
import {
    StyledFavoriteContainer,
    StyledItemSaveContainer,
    StyledPrintContainer,
    TitleMenuWrapper,
} from './helpers/styles';
import ImageRow from './ImageRow';
import PriceRow from './PriceRow';
import TitleRow from './TitleRow';

/** Establish connection to real-time auction events, only on the client-side */
const LiveAuctionEventsSubscriber = loadable(
    () => import('@/components/LiveEventSubscribers/LiveAuctionEventsSubscriber'),
    {
        ssr: false,
    }
);

type Props = {
    allowBidding?: boolean;
    className?: string;
    hideLiveButton?: boolean;
    hideLotNumbers?: boolean;
    hideSeller?: boolean;
    itemId: number;
    noPubNub?: boolean;
    noWidthLimit?: boolean;
    openOnBlank?: boolean;
    showBanner?: boolean;
    showBidStatus?: boolean;
    showCta?: boolean;
    showDate?: boolean;
    showEstimate?: boolean;
    showImageBorder?: boolean;
    showImageWithHover?: boolean;
    showSoldBadge?: boolean;
    showSubMenuTrigger?: boolean;
    style?: { [attribute: string]: string | number };
    trackClick?: (event: React.SyntheticEvent) => void;
};

const ItemCard = ({
    allowBidding,
    className = '',
    hideLiveButton,
    hideLotNumbers,
    hideSeller,
    itemId,
    noPubNub,
    noWidthLimit,
    openOnBlank,
    showBanner,
    showBidStatus,
    showCta,
    showDate,
    showEstimate,
    showImageBorder,
    showImageWithHover,
    showSoldBadge,
    showSubMenuTrigger,
    style = {},
    trackClick = noop,
}: Props) => {
    const dispatch = useAppDispatch();

    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const index = useAppSelector((state) => getItemSummaryIndex(state, itemId));
    const isCatalogOnly = useAppSelector((state) => getItemSummaryIsCatalogOnly(state, itemId));
    const promoted = useAppSelector((state) => getItemSummaryPromoted(state, itemId));
    const saleStartTs = useAppSelector((state) => getItemSummarySaleStartTs(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const title = useAppSelector((state) => getItemSummaryTitle(state, itemId));

    const bidders = useAppSelector((state) => getLiveCatalogBidders(state, catalogId));
    const absenteeBidders = useAppSelector((state) => getCatalogAbsenteeBidders(state, catalogId));
    const paused = useAppSelector((state) => getLiveCatalogPaused(state, catalogId));
    const currentItem = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const currentItemIndex = useAppSelector((state) => getLiveCatalogCurrentItemIndex(state, catalogId));
    const isLotOpen = currentItem === itemId;
    const comingUp = catalogStatus === 'live' && currentItemIndex <= index;
    const showLive = shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);
    const showSocial = allowBidding && showLive;
    const viewers = bidders + absenteeBidders || 1;

    const previousCatalogStatus = usePrevious(catalogStatus);

    React.useEffect(() => {
        if (previousCatalogStatus !== 'live' && catalogStatus === 'live') {
            if (allowBidding) {
                dispatch(fetchLiveCatalogStatusIfNeeded(catalogId, sellerId));
                dispatch(fetchCatalogPresence(catalogId));
            }
            dispatch(
                fetchLiveCatalogRegistrationByIdIfNeeded({
                    catalogId,
                    sellerId,
                })
            );
        }
    }, [allowBidding, catalogId, catalogStatus, sellerId, dispatch, previousCatalogStatus]);

    return (
        <StyledItemCard
            $open={isLotOpen && !noWidthLimit}
            $placeholder={!title}
            $showSocial={showSocial}
            className={className}
            data-testid="item-card"
            key={`${itemId}-noWidth-${noWidthLimit}`}
            onClick={trackClick}
            style={style}
        >
            {comingUp && allowBidding && !noPubNub && (
                <LiveAuctionEventsSubscriber
                    catalogId={catalogId}
                    limitMessagesFilter="itemCard"
                />
            )}
            {promoted && (
                <StyledBadge
                    $promoted={!noWidthLimit}
                    color="lightGrey"
                >
                    <FormattedMessage id="featured" />
                </StyledBadge>
            )}
            <StyledPrintContainer noWidthLimit={noWidthLimit}>
                <StyledFavoriteContainer data-testid="itemFavoriteIcon">
                    <StyledItemSaveContainer
                        $morePadding={!noWidthLimit}
                        itemId={itemId}
                    />
                </StyledFavoriteContainer>
                <ImageRow
                    itemId={itemId}
                    noWidthLimit={noWidthLimit}
                    openOnBlank={openOnBlank}
                    showBidStatus={showBidStatus}
                    showImageBorder={showImageBorder}
                    showImageWithHover={showImageWithHover}
                />
                <div>
                    {showSocial && isLotOpen && (
                        <ConsoleSocialBadge
                            className={cn('absolute top-0 m-[10px]', {
                                'mx-[10px] my-[20px]': !noWidthLimit,
                            })}
                            pause={paused}
                            viewers={viewers}
                        />
                    )}
                    {showSoldBadge && catalogStatus === AuctionStatus.Done && (
                        <StyledBadge
                            $promoted={!noWidthLimit}
                            color="darkGrey"
                        >
                            <FormattedMessage id="similarSoldItemCarousel.badge" />
                        </StyledBadge>
                    )}
                    <TitleMenuWrapper>
                        <TitleRow
                            hideLotNumbers={hideLotNumbers}
                            itemId={itemId}
                            openOnBlank={openOnBlank}
                        />
                        {showSubMenuTrigger && <ItemCardMenu itemId={itemId} />}
                    </TitleMenuWrapper>
                    <DetailsRow
                        allowBidding={allowBidding}
                        hideLiveButton={hideLiveButton}
                        hideSeller={hideSeller}
                        itemId={itemId}
                        showDate={showDate}
                        showEstimate={showEstimate}
                    />
                    <PriceRow itemId={itemId} />
                    <CardCta
                        allowBidding={allowBidding}
                        itemId={itemId}
                        showBanner={showBanner}
                        showCta={showCta}
                    />
                </div>
            </StyledPrintContainer>
        </StyledItemCard>
    );
};

export default React.memo<Props>(ItemCard);

interface CardProps {
    $open: boolean;
    $placeholder: boolean;
    $showSocial: boolean;
}

const StyledItemCard = styled.div<CardProps>`
    transition: all 0.15s;
    position: relative;
    display: block;
    ${({ $showSocial }) =>
        $showSocial &&
        `
        min-width: 150px;
        `}

    ${({ $open, theme }) =>
        $open &&
        `
         @media (min-width: 501px) {

                background-color: ${theme.colors.grey500};
                border-radius: 3px;
         }
    `}

    ${({ $placeholder, theme }) =>
        $placeholder &&
        `
        img {
            filter: blur(5px) !important;
        }

        div,
        span,
        h4 {
            color: transparent !important;
            text-shadow: 0 0 10px ${theme.colors.shadow500} !important;
        }
    `}
`;

interface PromotedProps {
    $promoted: boolean;
}

const StyledBadge = styled(InfoBadge)<PromotedProps>`
    position: absolute;
    top: 0;
    left: 0;
    ${({ $promoted }) =>
        $promoted &&
        `
            top: 10px;
            left: 10px;
        `}
`;
